import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CalendarAppointment } from "@/hooks/useCalendarData";
import { useTranslation } from "react-i18next";
import { Calendar } from "lucide-react";
import { useState, useRef } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { RescheduleConfirmContent, Conflict, Warning } from "./RescheduleConfirmContent";
import { RescheduleConfirmView } from "./RescheduleConfirmView";

// Re-export types for backwards compatibility
export type { Conflict, Warning } from "./RescheduleConfirmContent";

interface RescheduleConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appointment: CalendarAppointment | null;
  customerHasEmail?: boolean;
  newStartTime: Date;
  newEndTime: Date;
  timeZone?: string;
  teamMemberName?: string;
  conflicts?: Conflict[];
  warnings?: Warning[];
  onConfirm: (sendNotification: boolean) => void;
  onCancel?: () => void;
  originalStartTime?: Date;  // For visit drags, the actual visit start time
  originalEndTime?: Date;     // For visit drags, the actual visit end time
}

export const RescheduleConfirmDialog = ({
  open,
  onOpenChange,
  appointment,
  customerHasEmail = true,
  newStartTime,
  newEndTime,
  timeZone,
  teamMemberName,
  conflicts = [],
  warnings = [],
  onConfirm,
  onCancel,
  originalStartTime,
  originalEndTime,
}: RescheduleConfirmDialogProps) => {
  const { t } = useTranslation();
  const [sendNotification, setSendNotification] = useState(false);
  const explicitCloseRef = useRef(false);
  const isMobile = useIsMobile();

  if (!appointment) return null;
  if (!open) return null;

  // Check if there are any blocking conflicts
  const hasErrors = conflicts.some(c => c.severity === 'error');

  const handleConfirm = () => {
    explicitCloseRef.current = true;
    onConfirm(sendNotification);
  };

  const handleCancel = () => {
    explicitCloseRef.current = true;
    if (onCancel) {
      onCancel();
    }
    onOpenChange(false);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && !explicitCloseRef.current) {
      // Dialog closing without explicit confirm/cancel (e.g., Escape key, click outside)
      // Treat as cancel to revert optimistic update
      if (onCancel) {
        onCancel();
      }
    }

    // Reset flag for next time
    if (!open) {
      explicitCloseRef.current = false;
    }

    onOpenChange(open);
  };

  const contentProps = {
    appointment,
    customerHasEmail,
    newStartTime,
    newEndTime,
    timeZone,
    teamMemberName,
    conflicts,
    warnings,
    sendNotification,
    onSendNotificationChange: setSendNotification,
    onConfirm: handleConfirm,
    onCancel: handleCancel,
    originalStartTime,
    originalEndTime,
  };

  if (isMobile) {
    return <RescheduleConfirmView {...contentProps} />;
  }

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="sm:max-w-[550px]">
        <AlertDialogHeader className="space-y-4">
          <AlertDialogTitle className="flex items-center gap-3 text-xl">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10">
              <Calendar className="h-5 w-5 text-primary" />
            </div>
            {t('confirmReschedule')}
          </AlertDialogTitle>

          <AlertDialogDescription asChild>
            <div>
              <RescheduleConfirmContent {...contentProps} showButtons={false} />
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter className="gap-3 pt-6">
          <AlertDialogCancel
            onClick={handleCancel}
            className="px-6"
          >
            {t('cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={hasErrors}
            className="px-6"
          >
            <Calendar className="h-4 w-4 mr-2" />
            {hasErrors ? t('cannotReschedule') : t('confirmReschedule')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
